import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  TableConfig,
  TableData,
  TableColumn,
  PaginationInfo,
} from '@core/interfaces/table.interface';
import { SharedModule } from '@shared/shared.module';
import { MsDurationPipe } from '@core/pipes/ms-duration.pipe';

@Component({
  selector: 'app-dynamic-table',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, MsDurationPipe],
  templateUrl: './dynamic-table.component.html',
  styleUrls: [ './dynamic-table.component-1.scss', './dynamic-table.component-2.scss' ],
})
export class MultiDynamicTable implements OnInit, OnChanges {

  @Input() config!: TableConfig;
  @Input() columns: TableColumn[] = [];
  @Input() data: TableData[] = [];
  @Input() loading = false;
  @Input() idTable = '';

  @Output() buttonClick = new EventEmitter<{ action: string; data: any }>();
  @Output() linkClick = new EventEmitter<{ data: any }>();

  private allData: TableData[] = [];
  paginatedData: TableData[] = [];

  searchTerm = '';
  currentPage = 1;

  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50, 100];

  sortColumn = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  paginationInfo: PaginationInfo = {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    startIndex: 0,
    endIndex: 0,
  };

  ngOnInit(): void {
    if (!this.config) {
      this.config = {
        data: [],
        columns: [],
        pagination: {
          enabled: true,
          pageSize: 10,
          pageSizeOptions: [5, 10, 25, 50, 100],
        },
        filtering: {
          enabled: true,
          placeholder: 'Buscar ...',
        },
        sorting: {
          enabled: true,
        },
      };
    }
    this.setupTable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] || changes['data'] || changes['columns']) {
      this.setupTable();
    }
  }

  private setupTable(): void {
    if (this.columns && this.columns.length) {
      this.config.columns = [...this.columns];
    }

    if (this.data && this.data.length) {
      this.allData = [...this.data];
    } else if (this.config?.data && this.config?.data.length) {
      this.allData = [...this.config.data];
    } else {
      this.allData = [];
    }

    if (this.config?.pagination) {
      this.pageSize = this.config.pagination.pageSize;
      this.pageSizeOptions = this.config.pagination.pageSizeOptions;
    }

    this.applyFiltersAndPagination();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.applyFiltersAndPagination();
  }

  onSort(column: TableColumn): void {
    if (!column.sortable) return;

    if (this.sortColumn === column.key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column.key;
      this.sortDirection = 'asc';
    }

    this.applyFiltersAndPagination();
  }

  onPageSizeChange(): void {
    this.pageSize = +this.pageSize;
    this.currentPage = 1;
    this.applyFiltersAndPagination();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.applyFiltersAndPagination();
  }

  onButtonClick(action: string, data: any): void {
    this.buttonClick.emit({ action, data });
  }

  onLinkClick(data: any): void {
    this.linkClick.emit({ data });
  }

  private applyFiltersAndPagination(): void {
    let workingData = this.allData.filter((item) => {
      if (!this.searchTerm) return true;
      return Object.values(item).some(
        (value) =>
          value !== null &&
          value !== undefined &&
          value.toString().toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    });

    if (this.sortColumn) {
      workingData.sort((a, b) => {
        const av = a[this.sortColumn];
        const bv = b[this.sortColumn];
        return av < bv
          ? this.sortDirection === 'asc'
            ? -1
            : 1
          : av > bv
          ? this.sortDirection === 'asc'
            ? 1
            : -1
          : 0;
      });
    }

    const totalItems = workingData.length;
    const totalPages = Math.ceil(totalItems / this.pageSize);
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = Math.min(startIndex + this.pageSize, totalItems);

    this.paginationInfo = {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      totalItems,
      totalPages,
      startIndex,
      endIndex,
    };

    this.paginatedData = workingData.slice(startIndex, endIndex);
  }

  getCellValue(item: TableData, column: TableColumn): any {
    return item[column.key];
  }

  getBadgeClass(value: any, column: TableColumn): string {
    return column.badgeConfig?.colorMap?.[value] ?? 'so-bg-secondary';
  }

  getButtonClass(column: TableColumn): string {
    return column.buttonConfig?.class ?? 'btn-primary';
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString();
  }

  getPageNumbers(): number[] {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.paginationInfo.totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }
}
